// API Configuration
// All service URLs derive from REACT_APP_API_BASE_URL so switching between
// staging and production is a single env-var change:
//   staging:    REACT_APP_API_BASE_URL=https://stage.allroomie.com/api
//   production: REACT_APP_API_BASE_URL=https://allroomie.com/api
//
// In local development (NODE_ENV=development) with no env var set, relative
// paths are used so the CRA proxy (package.json "proxy") forwards requests.

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
const IS_PRODUCTION = process.env.NODE_ENV === 'production';
const USE_DEV_PROXY = process.env.REACT_APP_USE_DEV_PROXY !== 'false';

const deriveServiceUrl = (subpath: string): string => {
  if (!IS_PRODUCTION && USE_DEV_PROXY) {
    // In local dev, prefer CRA proxy to avoid browser CORS preflight issues.
    return `/api${subpath}`;
  }
  if (API_BASE) {
    // Env var set — build full URL (e.g. "https://stage.allroomie.com/api" + "/v1/user")
    return `${API_BASE}${subpath}`;
  }
  // No env var — use relative path for CRA dev proxy
  return `/api${subpath}`;
};

export const API_CONFIG = {
  BASE_URL: (!IS_PRODUCTION && USE_DEV_PROXY) ? '' : (API_BASE || 'http://localhost:8081'),

  AUTH_BASE_URL: deriveServiceUrl('/v1/user'),
  TENANT_BASE_URL: deriveServiceUrl('/v1/tenant'),
  MANAGEMENT_BASE_URL: deriveServiceUrl('/v1/management'),
  
  // Backend type
  BACKEND_TYPE: process.env.REACT_APP_BACKEND_TYPE || 'pms-service',
  
  // Request timeout
  TIMEOUT: 30000,
  
  // File upload settings
  MAX_FILE_SIZE: parseInt(process.env.REACT_APP_MAX_FILE_SIZE || '5242880'), // 5MB
  ALLOWED_FILE_TYPES: (process.env.REACT_APP_ALLOWED_FILE_TYPES || 'image/jpeg,image/png,image/gif,image/webp').split(','),
  
  // API endpoints for PMS Service
  ENDPOINTS: {
    // C-end Authentication (Customer-facing)
    LOGIN: '/api/user/auth/login',
    REGISTER: '/api/user/auth/register',
    GET_USER_INFO: '/api/user/auth/getInfo',
    REFRESH_TOKEN: '/api/user/auth/refresh/token',
    
    // Email verification endpoints (New Auth Service)
    AUTH_LOGOUT: '/auth/logout',
    EMAIL_CODE_SEND: '/auth/email/code/send',
    EMAIL_CODE_VERIFY: '/auth/email/code/verify',
    EMAIL_REGISTER: '/auth/email/register',
    EMAIL_LOGIN: '/auth/email/login',
    FORGOT_PASSWORD: '/auth/email/forget/password',
    
    // Google OAuth endpoints
    GOOGLE_LOGIN: '/google/login',
    GOOGLE_CALLBACK: '/google/callback',
    GOOGLE_HEALTH: '/google/health',
    GOOGLE_VERIFY: '/google/verify',
    
    // User Profile endpoints
    USER_PROFILE: '/userCenter/profile',
    USER_PROFILE_UPDATE: '/userCenter/profile/update',
    USER_PROFILE_AVATAR: '/userCenter/profile/avatar',
    USER_SMART_PROFILE: '/user/smart-profiles',
    
    // Original admin endpoints (fallback)
    ADMIN_LOGIN: '/login',
    ADMIN_REGISTER: '/register',
    ADMIN_LOGOUT: '/logout',
    ADMIN_GET_INFO: '/getInfo',
    
    // Buildings (Properties) - Old PMS endpoints
    BUILDINGS: '/pms/building',
    BUILDING_LIST: '/pms/building/list',
    BUILDING_DETAIL: '/pms/building', // + /{id}
    BUILDING_UPLOAD_IMAGE: '/pms/building/img/s3', // + /{id} - Used for B-side image uploads
    BUILDING_DELETE_PHOTO: '/pms/building/photo', // + /{id} - Used for B-side image deletion
    
    // Buildings (Properties) - New Tenant Service endpoints
    TENANT_BUILDING_DETAIL: '/public/building/detail', // + /{id}
    TENANT_BUILDINGS_BATCH: '/public/building/buildingsByBuildIds',
    
    // Units - New Tenant Service endpoints
    TENANT_UNIT_DETAIL: '/public/unit/detail', // + /{id}
    TENANT_APPLICATION_SUBMIT: '/tenant/applications',
    TENANT_SHARE_LISTING_EMAIL: '/tenant/mail/share-listing',
    TENANT_SHARE_BROKERAGE_CARD_EMAIL: '/tenant/mail/share-brokerage-card',
    
    // Units (Old PMS endpoints - deprecated, use Tenant Service endpoints below)
    UNITS: '/pms/unit',
    UNIT_LIST: '/pms/unit/list',
    UNIT_DETAIL: '/pms/unit', // + /{id}
    UNIT_WITH_ROOMS: '/pms/unit/withRooms', // + /{id}
    
    // Renter Clues (Customer leads)
    RENTER_CLUES: '/pms/RenterClue',
    RENTER_CLUE_LIST: '/pms/RenterClue/list',
    RENTER_CLUE_DETAIL: '/pms/RenterClue', // + /{id}
    
    // Agents
    AGENTS: '/pms/agent',
    AGENT_LIST: '/pms/agent/list',
    
    // Cities
    CITIES: '/pms/city',
    CITY_LIST: '/pms/city/list',
    
    // Approvals
    APPROVAL_LIST: '/pms/building/ApprovalList',
    APPROVAL_ACTION: '/pms/building/approval',
    
    // Captcha
    CAPTCHA: '/captchaImage'
  },
  
  // Request headers
  HEADERS: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache'
  }
};

// Helper function to build full API URL
export const buildApiUrl = (endpoint: string, params?: Record<string, any>): string => {
  let url = `${API_CONFIG.BASE_URL}${endpoint}`;
  
  if (params) {
    const queryString = Object.entries(params)
      .filter(([_, value]) => value !== undefined && value !== null)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&');
    
    if (queryString) {
      url += `?${queryString}`;
    }
  }
  
  return url;
};

// Helper function to build Auth Service API URL
export const buildAuthApiUrl = (endpoint: string, params?: Record<string, any>): string => {
  let url = `${API_CONFIG.AUTH_BASE_URL}${endpoint}`;
  
  if (params) {
    const queryString = Object.entries(params)
      .filter(([_, value]) => value !== undefined && value !== null)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&');
    
    if (queryString) {
      url += `?${queryString}`;
    }
  }
  
  return url;
};

// Helper function to build Tenant Service API URL
export const buildTenantApiUrl = (endpoint: string, params?: Record<string, any>): string => {
  let url = `${API_CONFIG.TENANT_BASE_URL}${endpoint}`;
  
  if (params) {
    const queryString = Object.entries(params)
      .filter(([_, value]) => value !== undefined && value !== null)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&');
    
    if (queryString) {
      url += `?${queryString}`;
    }
  }
  
  return url;
};

// Helper function to build Management Service API URL (B-side management APIs)
export const buildManagementApiUrl = (endpoint: string, params?: Record<string, any>): string => {
  let url = `${API_CONFIG.MANAGEMENT_BASE_URL}${endpoint}`;

  if (params) {
    const queryString = Object.entries(params)
      .filter(([_, value]) => value !== undefined && value !== null)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&');

    if (queryString) {
      url += `?${queryString}`;
    }
  }

  return url;
};

// Helper function to get auth headers
export const getAuthHeaders = () => {
  // Import dynamically to avoid circular dependency
  const secureStorage = require('../utils/secureStorage').default;
  const token = secureStorage.getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Helper function to generate unique request ID
export const generateRequestId = (): string => {
  // Generate unique ID: timestamp + random string
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 15);
  return `${timestamp}-${random}`;
};

// Helper function to transform frontend pagination to PMS pagination
export const transformPaginationParams = (frontendParams: { page?: number; size?: number }) => {
  return {
    pageNum: (frontendParams.page || 0) + 1, // PMS uses 1-based pagination
    pageSize: frontendParams.size || 10
  };
};

// Helper function to transform PMS response to frontend format
export const transformPmsResponse = (pmsResponse: any) => {
  // PMS typically returns data wrapped in { code, msg, data } format
  if (pmsResponse.code === 200) {
    return pmsResponse.data || pmsResponse;
  }
  throw new Error(pmsResponse.msg || 'API request failed');
};

// buildImageUrl — commented out: only caller was getBuildingsByCellV2 (MapboxLanding), which is inactive.
// Restore when map landing page is re-enabled.
/*
export const buildImageUrl = (imgUrl: string | null | undefined): string => {
  if (!imgUrl) {
    return '';
  }
  if (imgUrl.startsWith('http://') || imgUrl.startsWith('https://')) {
    return imgUrl;
  }
  return '';
};
*/
